import { useFieldArray, useFormContext } from 'react-hook-form';
import { lt } from '@mews/framework';
import { Typography, Button, IconName, Stack } from '@optimus-web/core';
import { useDialogForm } from '@mews-commander/form';
import { GenerateContractFormValues } from '../../types';
import { FORM_FIELD_PATHS } from '../../constants';
import { SelectedItemCard } from '../SelectedItemCard';
import { useCurrentEnterpriseAttachments } from '../../../../common/hooks/useCurrentEnterpriseAttachments';
import { UploadAttachmentForm, UploadFileFormValues } from './UploadAttachmentForm';

export const UploadAttachmentSection = () => {
    const attachments = useCurrentEnterpriseAttachments();

    const { watch, control } = useFormContext<GenerateContractFormValues>();
    const { fields, append, remove } = useFieldArray({ control, name: FORM_FIELD_PATHS.uploadedFiles });

    const selectedTemplates = watch(FORM_FIELD_PATHS.selectedTemplates);
    const isAtLeastOneTemplateSelected = Object.keys(selectedTemplates).length > 0;

    const uploadedFiles = watch(FORM_FIELD_PATHS.uploadedFiles);

    const attachmentLibraryNames = attachments?.map(({ customName }) => customName) || [];
    const locallyUploadedFileNames = uploadedFiles?.map(({ name }) => name) || [];
    const existingNames = [...attachmentLibraryNames, ...locallyUploadedFileNames];

    const addAttachment = (values: UploadFileFormValues) => {
        append({ ...values, expirationDateEnabled: false });
    };

    const [renderUploadForm, toggleUploadForm] = useDialogForm({
        title: lt('ContractsUploadFileLabel'),
        renderForm: (props) => (
            <UploadAttachmentForm addAttachment={addAttachment} existingNames={existingNames} {...props} />
        ),
    });

    return (
        <>
            <Stack vertical spacing="200">
                <Stack vertical spacing="100">
                    <Typography textStyle="titleMediumStrong" as="h2">
                        {lt('ContractsGenerateContractAdditionalFilesTitle')}
                    </Typography>
                    <Typography textStyle="bodyMedium" as="div">
                        {lt('ContractsGenerateContractAdditionalFilesDescription')}
                    </Typography>
                </Stack>

                <Stack vertical spacing="200">
                    {fields?.map(({ name }, index) => (
                        <SelectedItemCard
                            key={name}
                            name={name}
                            expirationDateFieldName={`${FORM_FIELD_PATHS.uploadedFiles}.${index}.expirationDate`}
                            expirationDateEnabledFieldName={`${FORM_FIELD_PATHS.uploadedFiles}.${index}.expirationDateEnabled`}
                            onRemove={() => remove(index)}
                        />
                    ))}
                </Stack>

                <Stack distribution="space-around">
                    <Button
                        disabled={!isAtLeastOneTemplateSelected}
                        icon={IconName.Upload}
                        variant="secondary"
                        type="button"
                        onClick={toggleUploadForm}
                    >
                        {lt(fields.length > 0 ? 'ContractsUploadAnotherFileLabel' : 'ContractsUploadFileLabel')}
                    </Button>
                </Stack>
            </Stack>
            {renderUploadForm()}
        </>
    );
};
